import React, { useEffect, useRef, useState, cloneElement, Children, FC, Fragment } from 'react';
import cx from 'classnames';
import { CaretV2Icon } from '@zola/zola-ui/src/components/SvgIcons/CaretV2';

import { usePrevious } from '@zola/zola-ui/src/hooks/usePrevious';
import useSlideHoverAnimation from '../useSlideHoverAnimation';

import 'components/navV2/NavDropdown/navDropdown.less';

type NavPrimaryDropdownProps = {
  ariaLabel?: string;
  className?: string;
  component?: keyof JSX.IntrinsicElements;
  showCaret?: boolean;
  isActive?: boolean;
  label: string | JSX.Element;
  link?: {
    href?: string;
    target?: string;
    rel?: string;
  };
  onClick: () => void;
  isMenuOpen: boolean;
  setIsMenuOpen: (isOpen: boolean, activeIndex: number | undefined) => void;
  setMenuTimerId: (timerId: number) => void;
  openMenuIndex?: number;
  index: number;
  activeNavItemIndex?: number;
  clearMenuTimer: () => void;
  children: React.ReactElement;
  icon?: string;
};

/**
 * NavPrimaryDropdown - HOC that passes a handleMenuClose prop to children
 */
const NavPrimaryDropdown: FC<NavPrimaryDropdownProps> = ({
  className,
  label,
  icon,
  ariaLabel = 'More Options',
  children,
  link,
  component: Component = 'div',
  showCaret = true,
  isActive = false,
  onClick,
  isMenuOpen,
  setIsMenuOpen,
  setMenuTimerId,
  openMenuIndex,
  index,
  activeNavItemIndex,
  clearMenuTimer,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const [isFadeIn, setFadeIn] = useState(false);
  const prevOpenMenuIndex = usePrevious(openMenuIndex);
  const animation = useSlideHoverAnimation({
    prevHoverIndex: prevOpenMenuIndex,
    curHoverIndex: openMenuIndex,
    index,
  });
  const menuCloseTimer = useRef<number>();
  const menuDelayTimer = useRef<number>();

  const HOVER_DELAY_MS = 300;
  const FADE_OUT_ANIMATION_DURATION_MS = 200;

  const handleMenuOpen = () => {
    if (isMenuOpen) {
      window.setTimeout(() => {
        // Delay clearing the timer so react has a chance to update.
        clearMenuTimer();
      }, 0);
      clearTimeout(menuCloseTimer.current);
      setIsMenuOpen(true, index); // needed to update the open menu index
      setFadeIn(true);
      return setShowMenu(true);
    }
    menuDelayTimer.current = window.setTimeout(() => {
      setFadeIn(true);
      setIsMenuOpen(true, index);
      setShowMenu(true);
    }, HOVER_DELAY_MS);
    return menuDelayTimer;
  };

  const handleMenuClose = (): void => {
    clearTimeout(menuDelayTimer.current);
    setFadeIn(false);
    const isMenuOpenTimerId = window.setTimeout(() => {
      // Index change triggers slideOut animation
      setIsMenuOpen(false, activeNavItemIndex);
    }, 100);
    setMenuTimerId(isMenuOpenTimerId);
    menuCloseTimer.current = window.setTimeout(() => {
      setShowMenu(false);
    }, FADE_OUT_ANIMATION_DURATION_MS);
  };

  useEffect(
    () => () => {
      clearTimeout(menuCloseTimer.current);
      clearTimeout(menuDelayTimer.current);
    },
    [menuCloseTimer, menuDelayTimer]
  );

  const renderLabelComponent = () => {
    const elementAttrs: React.AnchorHTMLAttributes<HTMLAnchorElement> & {
      'data-testid'?: string;
    } = {
      'aria-label': ariaLabel,
      'data-testid': 'nav-dropdown__label',
      target: '_self',
      ...link,
      onClick,
      onMouseEnter: handleMenuOpen,
    };

    const activeConditions = (isActive && !isMenuOpen) || showMenu;
    elementAttrs.className = cx(
      'nav-dropdown__label',
      'nav-dropdown__label--primaryNav',
      'nav-dropdown__label--link',
      animation,
      {
        'nav-dropdown__label--showMenu': showMenu,
        'nav-dropdown__label--active': activeConditions,
      }
    );

    return (
      <a {...elementAttrs}>
        {icon && <img src={icon} className="nav-dropdown__label--icon" alt="" />}
        {label}
        {showCaret && (
          <CaretV2Icon title="Caret" width={8} height={5} className="nav-dropdown__chevron" />
        )}
      </a>
    );
  };

  const injectedChildren = Children.map(
    children as React.ReactElement,
    (child: React.ReactElement) => {
      if (child?.type === 'div') {
        return child;
      }
      return cloneElement(child, { handleMenuClose });
    }
  );

  return (
    <Fragment>
      <Component
        className={cx('nav-dropdown', className)}
        data-testid="nav-dropdown"
        onMouseLeave={handleMenuClose}
      >
        {renderLabelComponent()}
        <div
          onClick={handleMenuClose}
          className={cx('nav-dropdown__children--megaMenu', {
            'nav-dropdown__children--fadeIn': isFadeIn,
            'nav-dropdown__children--fadeOut': !isFadeIn,
            'nav-dropdown__children--isHidden': !showMenu,
          })}
          data-testid="nav-dropdown__children"
        >
          {showMenu && injectedChildren}
        </div>
      </Component>
    </Fragment>
  );
};

export default NavPrimaryDropdown;
